import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import { theme } from "../../styles/theme";

const GameContainer = styled.section`
  position: relative;
  background-image: url("/assets/images/skybackground.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: ${theme.spacing.xxxl};
  margin: 0;

  @media (max-width: 1920px) {
    margin-top: -32px;
  }

  @media (max-width: 1440px) {
    margin-top: -32px;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.xl};
    background-size: auto;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.xl};
    height: auto;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TitleImage = styled.img`
  width: 100%;
  max-width: 400px;
  height: auto;
  margin-bottom: ${theme.spacing.xxl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    max-width: 300px;
    margin-bottom: ${theme.spacing.xl};
  }
`;

const CommentCopy = styled.div`
  position: absolute;
  top: 8%;
  font-size: 2.2rem;
  color: ${theme.colors.text.primary};
  text-align: center;
  letter-spacing: 0.05em;
  white-space: nowrap;
  z-index: 2;

  @media (max-width: 2560px) {
    top: 9%;
    font-size: 1.8rem;
  }

  @media (max-width: 1440px) {
    top: 9%;
    font-size: 1.8rem;
  }

  @media (max-width: ${theme.breakpoints.desktop}) {
    top: 190px;
    font-size: 1.2rem;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    top: 130px;
    font-size: 0.9rem;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    top: 120px;
    font-size: 0.85rem;
  }

  @media (max-width: 320px) {
    top: 12%;
    font-size: 0.7rem;
  }
`;

const ScenariosContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.xxxl};
  width: 100%;
  margin-top: 40px;
  margin-bottom: ${theme.spacing.xxl};
  height: auto;

  @media (max-width: ${theme.breakpoints.desktop}) {
    margin-top: 0;
    gap: ${theme.spacing.xl};
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    margin-top: 0;
    gap: ${theme.spacing.xl};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-top: 0px;
    gap: ${theme.spacing.xl};
  }

  @media (max-width: 425px) {
    margin-top: 10px;
    gap: ${theme.spacing.xl};
  }

  @media (max-width: 375px) {
    margin-top: 20px;
    gap: ${theme.spacing.xl};
  }

  @media (max-width: 320px) {
    margin-top: 10px;
    gap: ${theme.spacing.xl};
  }
`;

const ScenarioCard = styled.div<{ bgImage: string; className?: string }>`
  position: relative;
  background-image: url(${(props) => props.bgImage});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  width: 100%;
  max-width: 800px;
  aspect-ratio: 980 / 1000;
  display: flex;
  padding: 0 ${theme.spacing.xxl};
  overflow: hidden;

  @media (max-width: ${theme.breakpoints.tablet}) {
    max-width: 600px;
    padding: ${theme.spacing.lg};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    max-width: 500px;
    padding: ${theme.spacing.md};
  }

  @media (max-width: 390px) {
    max-width: 100%;
    padding: ${theme.spacing.sm};
  }
`;

const CardContentContainer = styled.div`
  position: absolute;
  width: 85%;
  height: 65%;
  top: 49%;
  left: 49%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: row;
`;

const LeftContainer = styled.div`
  position: relative;
  width: 45%;
  height: 100%;
  left: 0;
  top: 0;
  z-index: 2;
`;

const RightContainer = styled.div`
  position: relative;
  width: 55%;
  height: 100%;
  right: 0;
  top: 0;
  z-index: 2;
`;

const VideoArea = styled.div<{
  isTouchDevice: boolean;
  onTouchStart?: () => void;
}>`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background: transparent;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  &:hover button {
    opacity: ${(props) => (props.isTouchDevice ? "inherit" : "1")};
  }

  &:active button {
    opacity: 1;
  }
`;

const VideoElement = styled.video<{ hasPlayed: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  background-color: transparent;
  opacity: 1;
  transition: opacity 0.3s ease;

  &:focus {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

const CustomVideoButton = styled.button<{
  hasPlayed: boolean;
  isPlaying: boolean;
  isTouchDevice: boolean;
  isVisible: boolean;
}>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 50px;
  height: 50px;
  background: rgba(128, 128, 128, 0.8);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  z-index: 10;
  transition: transform 0.2s ease, opacity 0.3s ease, background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(props) => {
    if (!props.hasPlayed) return 1; // 初回は常に表示
    if (props.isTouchDevice) return props.isVisible ? 1 : 0; // モバイルは状態制御
    return 0; // デスクトップはhoverで制御
  }};

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
    background: rgba(128, 128, 128, 0.9);
    opacity: 1;
  }

  &:active {
    transform: translate(-50%, -50%) scale(0.95);
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 30px;
    height: 30px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 30px;
    height: 30px;
  }

  @media (max-width: 380px) {
    width: 30px;
    height: 30px;
  }
`;

const PlayIcon = styled.div`
  width: 0;
  height: 0;
  border-left: 20px solid white;
  border-top: 12px solid transparent;
  border-bottom: 12px solid transparent;
  margin-left: 4px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    border-left: 16px solid white;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    margin-left: 3px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    border-left: 12px solid white;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    margin-left: 2px;
  }
`;

const PauseIcon = styled.div`
  display: flex;
  gap: 4px;

  &::before,
  &::after {
    content: "";
    width: 6px;
    height: 20px;
    background: white;
    border-radius: 1px;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    gap: 3px;

    &::before,
    &::after {
      width: 5px;
      height: 16px;
    }
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 2px;

    &::before,
    &::after {
      width: 4px;
      height: 12px;
    }
  }
`;

const BannerCharacterGirl = styled.img`
  position: absolute;
  width: 60%;
  height: auto;
  right: -47px;
  bottom: -1px;
  z-index: 2;

  @media (max-width: 1440px) {
    width: 60%;
  }

  @media (max-width: ${theme.breakpoints.desktop}) {
    width: 60%;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 60%;
    right: -36px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 40%;
    right: -16px;
  }

  @media (max-width: 390px) {
    width: 40%;
    right: -14px;
  }

  @media (max-width: 320px) {
    width: 40%;
    right: -12px;
  }
`;

const BannerCharacterBird = styled.img`
  position: absolute;
  width: 44%;
  height: auto;
  right: -11px;
  bottom: -1px;
  z-index: 3;

  @media (max-width: ${theme.breakpoints.desktop}) {
    width: 44%;
  }

  @media (max-width: 834px) {
    width: 32%;
    right: 0;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 39%;
    right: -7px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 30%;
    right: -3px;
  }

  @media (max-width: 393px) {
    width: 30%;
    right: -2px;
  }

  @media (max-width: 320px) {
    width: 30%;
    right: -3px;
  }
`;

const BannerImage = styled.img`
  position: absolute;
  width: 91%;
  height: auto;
  top: 0;
  right: 0;
  z-index: 2;
`;

const ScenarioDescription = styled.div`
  position: absolute;
  right: 0;
  top: 15%;
  width: 90%;
  z-index: 2;

  p {
    color: ${theme.colors.text.primary};
    font-size: 1.2rem;
    line-height: 1.5;
    white-space: pre-line;
  }

  @media (max-width: ${theme.breakpoints.desktop}) {
    p {
      font-size: 1.2rem;
      line-height: 1.5;
    }
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    p {
      font-size: 1.1rem;
      line-height: 1.4;
    }
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    p {
      font-size: 0.8rem;
      line-height: 1.5;
    }
  }

  @media (max-width: 393px) {
    p {
      font-size: 0.7rem;
      line-height: 1.4;
    }
  }

  @media (max-width: 320px) {
    p {
      font-size: 0.6rem;
      line-height: 1.3;
    }
  }
`;

const GameStartButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  img {
    width: 250px;
    height: auto;
    display: block;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    img {
      width: 200px;
    }
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    img {
      width: 180px;
    }
  }
`;

const GameSection: React.FC = () => {
  const snsVideoRef = useRef<HTMLVideoElement>(null);
  const smsVideoRef = useRef<HTMLVideoElement>(null);
  const [snsIsPlaying, setSnsIsPlaying] = useState(false);
  const [smsIsPlaying, setSmsIsPlaying] = useState(false);
  const [snsHasPlayed, setSnsHasPlayed] = useState(false);
  const [smsHasPlayed, setSmsHasPlayed] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [snsButtonVisible, setSnsButtonVisible] = useState(false);
  const [smsButtonVisible, setSmsButtonVisible] = useState(false);

  // タッチデバイス検出
  useEffect(() => {
    const checkTouchDevice = () => {
      setIsTouchDevice(
        "ontouchstart" in window || navigator.maxTouchPoints > 0
      );
    };
    checkTouchDevice();
    window.addEventListener("resize", checkTouchDevice);
    return () => window.removeEventListener("resize", checkTouchDevice);
  }, []);

  // iOS Safari対応：動画の自動停止を検知してボタン状態を同期
  useEffect(() => {
    const snsVideo = snsVideoRef.current;
    const smsVideo = smsVideoRef.current;

    const handleSnsVideoPause = () => {
      setSnsIsPlaying(false);
    };

    const handleSmsVideoPause = () => {
      setSmsIsPlaying(false);
    };

    const handleSnsVideoPlay = () => {
      setSnsIsPlaying(true);
    };

    const handleSmsVideoPlay = () => {
      setSmsIsPlaying(true);
    };

    if (snsVideo) {
      snsVideo.addEventListener("pause", handleSnsVideoPause);
      snsVideo.addEventListener("play", handleSnsVideoPlay);
    }
    if (smsVideo) {
      smsVideo.addEventListener("pause", handleSmsVideoPause);
      smsVideo.addEventListener("play", handleSmsVideoPlay);
    }

    return () => {
      if (snsVideo) {
        snsVideo.removeEventListener("pause", handleSnsVideoPause);
        snsVideo.removeEventListener("play", handleSnsVideoPlay);
      }
      if (smsVideo) {
        smsVideo.removeEventListener("pause", handleSmsVideoPause);
        smsVideo.removeEventListener("play", handleSmsVideoPlay);
      }
    };
  }, []);

  // モバイルでのボタン表示制御
  const showButtonTemporarily = (
    setButtonVisible: (visible: boolean) => void
  ) => {
    if (isTouchDevice) {
      setButtonVisible(true);
      setTimeout(() => setButtonVisible(false), 2000); // 2秒後に隠す
    }
  };

  const handleVideoClick = (
    videoRef: React.RefObject<HTMLVideoElement | null>,
    scenario: string,
    setPlaying: (playing: boolean) => void,
    setHasPlayed: (hasPlayed: boolean) => void,
    setButtonVisible: (visible: boolean) => void
  ) => {
    const video = videoRef.current;
    if (video) {
      if (video.paused) {
        video.play();
        setPlaying(true);
        setHasPlayed(true);
      } else {
        video.pause();
        setPlaying(false);
      }
      // モバイルでボタンを一時的に表示
      showButtonTemporarily(setButtonVisible);
    }
  };

  const handleVideoEnded = (
    setPlaying: (playing: boolean) => void,
    scenario: string
  ) => {
    setPlaying(false);
  };

  const handleGameStart = () => {
    // 新しいタブでゲームページを開く
    window.open(
      "https://go.trendmicro.com/jp/forHome/mktcp/bosagi/game/",
      "_blank"
    );
  };

  return (
    <GameContainer id="game">
      <Container>
        <TitleImage
          src="/assets/images/sag_title.png"
          alt="サギトレ"
          loading="lazy"
        />
        <CommentCopy>
          チャレンジするゲームは二つ！
          <br />
          あなたの防サギ力はどのランク？
        </CommentCopy>
        <ScenariosContainer>
          <ScenarioCard
            bgImage="/assets/images/sns_sagi_bg.png"
            className="sns_sagi"
          >
            <CardContentContainer>
              <LeftContainer>
                <VideoArea
                  className="sns_video_area"
                  isTouchDevice={isTouchDevice}
                  onTouchStart={() =>
                    showButtonTemporarily(setSnsButtonVisible)
                  }
                >
                  <VideoElement
                    ref={snsVideoRef}
                    hasPlayed={snsHasPlayed}
                    playsInline
                    preload="metadata"
                    poster="/assets/images/video_sns_poster.png"
                    onEnded={() =>
                      handleVideoEnded(setSnsIsPlaying, "SNS型投資詐欺編")
                    }
                  >
                    <source
                      src="/assets/images/video_sns.mp4"
                      type="video/mp4"
                    />
                    お使いのブラウザはビデオタグをサポートしていません。
                  </VideoElement>
                  <CustomVideoButton
                    hasPlayed={snsHasPlayed}
                    isPlaying={snsIsPlaying}
                    isTouchDevice={isTouchDevice}
                    isVisible={snsButtonVisible}
                    onClick={() =>
                      handleVideoClick(
                        snsVideoRef,
                        "SNS型投資詐欺編",
                        setSnsIsPlaying,
                        setSnsHasPlayed,
                        setSnsButtonVisible
                      )
                    }
                    aria-label={snsIsPlaying ? "動画を一時停止" : "動画を再生"}
                  >
                    {snsIsPlaying ? <PauseIcon /> : <PlayIcon />}
                  </CustomVideoButton>
                </VideoArea>
              </LeftContainer>
              <RightContainer>
                <BannerImage
                  src="/assets/images/sns_sagi_banner.png"
                  alt="SNS型投資詐欺編バナー"
                  className="sns-banner"
                  loading="lazy"
                />
                <ScenarioDescription className="scenario-description">
                  <p>
                    「今が投資のチャンス！」ミーアさんはSNSで有名な"先生"にそう言われて入金した結果、お金がすべてなくなってしまいました…
                    <br />
                    ミーアさんは一体どのようにして詐欺にあってしまったのでしょうか…
                  </p>
                </ScenarioDescription>

                <BannerCharacterGirl
                  src="/assets/images/sns_sagi_girl.png"
                  alt="SNS型投資詐欺編キャラクター"
                  loading="lazy"
                />
              </RightContainer>
            </CardContentContainer>
          </ScenarioCard>

          <ScenarioCard
            bgImage="/assets/images/sms_phishing_bg.png"
            className="sms_phishing"
          >
            <CardContentContainer>
              <LeftContainer>
                <VideoArea
                  className="sms_video_area"
                  isTouchDevice={isTouchDevice}
                  onTouchStart={() =>
                    showButtonTemporarily(setSmsButtonVisible)
                  }
                >
                  <VideoElement
                    ref={smsVideoRef}
                    hasPlayed={smsHasPlayed}
                    playsInline
                    preload="metadata"
                    poster="/assets/images/video_sms_poster.png"
                    onEnded={() =>
                      handleVideoEnded(setSmsIsPlaying, "SMSフィッシング編")
                    }
                  >
                    <source
                      src="/assets/images/video_sms.mp4"
                      type="video/mp4"
                    />
                    お使いのブラウザはビデオタグをサポートしていません。
                  </VideoElement>
                  <CustomVideoButton
                    hasPlayed={smsHasPlayed}
                    isPlaying={smsIsPlaying}
                    isTouchDevice={isTouchDevice}
                    isVisible={smsButtonVisible}
                    onClick={() =>
                      handleVideoClick(
                        smsVideoRef,
                        "SMSフィッシング編",
                        setSmsIsPlaying,
                        setSmsHasPlayed,
                        setSmsButtonVisible
                      )
                    }
                    aria-label={smsIsPlaying ? "動画を一時停止" : "動画を再生"}
                  >
                    {smsIsPlaying ? <PauseIcon /> : <PlayIcon />}
                  </CustomVideoButton>
                </VideoArea>
              </LeftContainer>
              <RightContainer>
                <BannerImage
                  src="/assets/images/sms_phishing_banner.png"
                  alt="SMSフィッシング編バナー"
                  className="sms-banner"
                  loading="lazy"
                />
                <ScenarioDescription className="scenario-description">
                  <p>
                    ある日届いた銀行からのSMS（ショートメッセージ）…いつものようにURLを開いてIDとパスワードを入力すると口座の中身がなくなってしまった！詐欺には気をつけていたハシビロさんですが、いったいどのような手口で詐欺にだまされてしまったのでしょうか…
                  </p>
                </ScenarioDescription>
                <BannerCharacterBird
                  src="/assets/images/sms_phishing_bird.png"
                  alt="SMSフィッシング編キャラクター"
                  loading="lazy"
                />
              </RightContainer>
            </CardContentContainer>
          </ScenarioCard>
        </ScenariosContainer>

        <GameStartButton onClick={handleGameStart}>
          <img
            src="/assets/images/gamestart.png"
            alt="今すぐプレイ"
            loading="lazy"
          />
        </GameStartButton>
      </Container>
    </GameContainer>
  );
};

export default GameSection;
